import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomSelect from 'components/inputs/CustomSelect';
import SubmitButton from 'components/SubmitButton';
import { AppDispatch, RootState } from 'store/store';
import { Program } from 'types/program';
import { ClientVisitThunks } from './clientVisitThunks';
import { ClientVisit } from '../../../types/clientVisit';
import DateInput from '../../../components/inputs/DateInput';
import { setIsEditing } from '../../payer/payerSlice';
import XIcon from '../../../assets/images/icons/XIcon';
import {ProgramService} from '../../../services/programService';

interface ClientVisitFormProps {
  closeDrawer: () => void;
  clientId?: string;
}

const ClientVisitForm: React.FC<ClientVisitFormProps> = ({ closeDrawer, clientId }) => {
  const clientVisit = useSelector((state: RootState) => state.clientVisit.clientVisit);
  const dispatch = useDispatch<AppDispatch>();
  const [updatedClientVisit, setUpdatedClientVisit] = useState({ ...clientVisit });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setUpdatedClientVisit({ ...clientVisit });
  }, [clientVisit]);

  const [programOptions, setProgramOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const isSaveDisabled = !updatedClientVisit.programId || !updatedClientVisit.visitDate;

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true);
      try {
          const programsResponse = await  ProgramService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        const programOpts =
          programsResponse.data?.map((program: Program) => ({
            label: program.name,
            value: program.id,
          })) || [];
        setProgramOptions(programOpts);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const handleClose = () => {
    dispatch(setIsEditing(false));
    closeDrawer();
  };


  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { id: string; value: any } },
  ) => {
    const { id, value } = e.target;
    if (!id) return;
    setUpdatedClientVisit((prev: any) => ({ ...prev, [id]: value }));
  };

  const handleProgramChange = (selectedOption: any) => {
    setUpdatedClientVisit((prev: any) => ({ ...prev, programId: selectedOption ? selectedOption.value : '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Ensure clientId is included for new visits
    const visitData = {
      ...updatedClientVisit,
      clientId: clientId || updatedClientVisit.clientId
    };
    
    if (clientVisit?.id) {
      await dispatch(ClientVisitThunks.update(new ClientVisit(visitData))).then(() => closeDrawer());
    } else {
      await dispatch(ClientVisitThunks.create(new ClientVisit(visitData))).then(() => closeDrawer());
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {clientVisit?.id ? 'Edit visit' : 'Create new visit'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {clientVisit?.id ? 'Edit' : 'Create a new'} client visit
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="flex-1 px-6 pb-6 ">
          <div className="h-full ">
            <div className="mt-5">
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col space-y-6 items-center mt-7">
                  <div className="w-full">
                    <label className="block text-slate-800 text-sm font-semibold mb-1">
                      Program
                    </label>
                    <CustomSelect
                      options={programOptions}
                      isMulti={false}
                      value={updatedClientVisit.programId}
                      onChange={handleProgramChange}
                    />
                  </div>
                  <div className="w-full">
                    <DateInput
                      label="Date"
                      type="date"
                      id="visitDate"
                      value={updatedClientVisit.visitDate}
                      onChange={handleChange}
                      placeholder="MM/DD/YYYY"
                      allowPastDates={true}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-5 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex justify-end">
            <SubmitButton
              type="submit"
              onClick={handleSubmit}
              label={clientVisit?.id ? 'Save visit' : 'Create visit'}
              disabled={isSaveDisabled}
            />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientVisitForm;
